/**
 * Facility Schedule — full table per Figma.
 *
 * Column groups:
 *   1. Identity (sticky left): Flag, #, Facility Name
 *   2. Location: Acquired, Operator, Address, City, State, Zip, Geo, County
 *   3. Retro dates: PL Retro Date, GL Retro Date
 *   4. Retention + limits: Retention (Money), PL Limit, GL Limit
 *   5. Census + derived: Occupancy (derived), Skilled, Assisted,
 *      Independent, Add'l Exposure, Equivalency (derived),
 *      Rate (derived), Unadj. Prem. (derived), Mat. Credit,
 *      Mat. Adj. Prem. (derived), SIR Credit (derived),
 *      SIR Adj. Prem. (derived), Lic. SNF/ALF/ILF, Total Lic. (derived)
 *   6. Actions (sticky right): trash
 *
 * Data source: every row binds to a `Facility` in the shared SoT
 * (`useFacilities(submissionId)`). Edits in any cell fire a mutation
 * that propagates to UW Information → Schedule of Location (which
 * subscribes to the same SoT). Derived columns recompute live via
 * `useFacilitiesCalc` from `domain/pricing`.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { Building07 } from "@untitledui/icons";
import { Button, SearchSelect, Select, Table, Tooltip, TooltipTrigger } from "@tricura/atlas-ui";
import type { CatalogFacility, ZipCode } from "../../../../api";
import { EditableCellProvider, EditableDateCell, EditableTextCell, InvalidShell } from "../../../../components/editable-cell";
import { FacilityFlagPopover } from "../../../../components/facility-flag-popover";
import { ARCHIVED_ROW, ARCHIVED_TOOLTIP, ARCHIVED_TOOLTIP_TRIGGER } from "../../../../components/facility-archive-row";
import { FacilityRowActionsMenu } from "../../../../components/facility-row-actions-menu";
import { sortArchivedLast } from "../../../../domain/facility-archive";
import { CountySelect } from "../../../../components/county-select";
import { FacilityNameAutocomplete } from "../../../../components/facility-name-autocomplete";
import { ZipCodeAutocomplete } from "../../../../components/zip-code-autocomplete";
import { FormGroup } from "../../../../components/form-group";
import { MoneySelect } from "../../../../components/money-select";
import { SelectWithCustom } from "../../../../components/select-with-custom";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { useDeferredSetter } from "../../../../hooks/use-deferred-setter";
import { useFacilities } from "../../../../hooks/use-facilities";
import { useMaturityCredits } from "../../../../hooks/use-maturity-credits";
import type { Facility, FacilityWritePayload } from "../../../../types/facility";
import { formDateToIso } from "../../../../api/adapters/submission";
import { formatMoneyMask, moneyDigits } from "../../../../utils/money";
import { useStates } from "../../../../hooks/use-states";
import { useSubmissionContext } from "../../context";
import { useFacilitiesCalc } from "../use-facility-calc";

// Date cells (Acquired / PL Retro / GL Retro) flag the same input the
// backend would silently drop to null on save — `facilities.ts` converts
// these with this exact `formDateToIso`, so a non-empty value it can't
// parse (e.g. "00/00/0000" or a half-typed date) shows the inline error
// instead of being lost. (OR-1281)
const DATE_FORMAT_ERROR = "The date format is incorrect. Please enter the date as MM/DD/YYYY";
const isInvalidDate = (v: string) => v.trim() !== "" && formDateToIso(v) === null;

const GEO_ITEMS = [
    { id: "Rural", label: "Rural" },
    { id: "Urban", label: "Urban" },
];

const RETENTION_PRESETS = [0, 25_000, 50_000, 75_000, 100_000, 200_000, 250_000];

const W = {
    rowNum: 48,
    // Fixed 240px (Andressa, jun-22): the frozen Facility Name column stays
    // narrow; the name scrolls horizontally while editing and wraps to
    // multiple lines once saved (see FacilityNameAutocomplete).
    facilityName: 240,
    providerNumber: 220,
    acquired: 200,
    operator: 200,
    address: 320,
    city: 160,
    state: 160,
    zip: 160,
    geo: 160,
    county: 200,
    plRetro: 200,
    glRetro: 200,
    retention: 220,
    plLimit: 180,
    glLimit: 180,
    occupancy: 140,
    skilled: 120,
    assisted: 120,
    independent: 140,
    addlExposure: 160,
    equivalency: 140,
    rate: 140,
    unadjPrem: 160,
    matCredit: 220,
    matAdjPrem: 160,
    sirCredit: 140,
    sirAdjPrem: 160,
    licSnf: 120,
    licAlf: 120,
    licIlf: 120,
    totalLic: 140,
    actions: 64,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });

// Mobile (`<md`): drop the sticky pinning so the table scrolls as one
// block — the frozen #/Facility/actions columns blanket a narrow
// viewport (OR-1280). Desktop (`md+`) unchanged. Matches Staffing Ratios.
//
// The Facility Name column's left offset is the Tailwind class `md:left-[48px]`
// (= `W.rowNum`, the # column width) so it ONLY applies at md+. Passing it
// inline (`left: 48`) instead leaks onto mobile: atlas cells default to
// `position: relative`, so a non-zero inline `left` shifts the column right
// even when not sticky — leaving a 48px gap after # and overlapping Provider.
const STICKY_NUM_HEAD = "md:sticky md:left-0 md:z-10 md:bg-secondary";
const STICKY_NAME_HEAD = "md:sticky md:left-[48px] md:z-10 md:bg-secondary";
const STICKY_NUM_CELL = "md:sticky md:left-0 md:z-10 md:bg-primary md:text-tertiary";
const STICKY_NAME_CELL = "md:sticky md:left-[48px] md:z-10 md:bg-primary";
const STICKY_RIGHT_HEAD = "md:sticky md:right-0 md:z-10 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";
const STICKY_RIGHT_CELL = "md:sticky md:right-0 md:z-10 md:bg-primary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";

// ─────────── helpers ──────────────────────────────────────

const integer = (raw: string) => raw.replace(/\D/g, "");

/** Maturity-adjustment fraction (0.43) → preset id/label ("43" / "43%").
 *  `toFixed(4)` strips float artifacts (0.8 × 100 = 80.0000…1) and keeps
 *  three-decimal factors (99.215%) matching `maturityCreditToPercent`. */
function maturityPreset(fraction: number): { id: string; label: string } {
    const pct = Number((fraction * 100).toFixed(4));
    return { id: String(pct), label: `${pct}%` };
}

function num(v: string | undefined): number {
    if (!v) return 0;
    const n = Number(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function displayMoney(v: number | string | null | undefined): string {
    const n = typeof v === "number" ? v : num(v ?? undefined);
    if (!Number.isFinite(n) || n === 0) return "—";
    return `$${Math.round(n).toLocaleString("en-US")}`;
}

/** Money with cents (e.g. $14,673.93) — for columns where precision matters,
 *  like Unadjusted Premium (Linda, jun-23). */
function displayMoneyCents(v: number | string | null | undefined): string {
    const n = typeof v === "number" ? v : num(v ?? undefined);
    if (!Number.isFinite(n) || n === 0) return "—";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function displayInt(v: number | string | null | undefined): string {
    const n = typeof v === "number" ? v : num(v ?? undefined);
    if (!Number.isFinite(n) || n === 0) return "—";
    return n.toLocaleString("en-US");
}

function displayDecimal(v: number | string | null | undefined, digits = 1): string {
    const n = typeof v === "number" ? v : num(v ?? undefined);
    if (!Number.isFinite(n) || n === 0) return "—";
    return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * Render a 0–1 fraction as a percent string. Used by derived cells
 * (Occupancy, SIR Credit) where `null` means "couldn't compute"
 * (renders "—") but `0` is a legitimate value (renders "0%").
 */
function displayPercentFraction(v: number | null | undefined, digits = 0): string {
    if (v === null || v === undefined || !Number.isFinite(v)) return "—";
    return `${(v * 100).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

const READONLY_CELL = "text-secondary text-sm tabular-nums";

/** Stable reference for the not-yet-loaded case — see `facilities` below. */
const EMPTY_FACILITIES: Facility[] = [];

// ─────────── component ────────────────────────────────────

export function FacilityScheduleSection() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const { state, create, update, remove } = useFacilities(detail.id);

    /** Render shorthand — empty list while loading or on error so the
     *  empty-state overlay shows and the table chrome stays consistent.
     *  Memoized against a stable empty array so the row-ordering memo below
     *  isn't invalidated on every render by a fresh `[]`. */
    const facilities = useMemo(
        () => (state.status === "ready" ? state.data : EMPTY_FACILITIES),
        [state],
    );

    /** One pricing result per facility row, derived live from the SoT
     *  + the rates table. All downstream cells (occupancy, equivalency,
     *  rate, premiums) render from this array — the SoT doesn't
     *  persist any of these values. */
    const calcs = useFacilitiesCalc(facilities);

    // Maturity Credit dropdown options, loaded from the backend
    // (`/v2/credit-factors/claims-made`) → percentages, default 100%.
    const maturityQuery = useMaturityCredits();
    const maturityPresets = useMemo(
        () => (maturityQuery.status === "ready" ? maturityQuery.data.map((f) => maturityPreset(f.maturityAdjustment)) : []),
        [maturityQuery],
    );
    // Default-selected Maturity Credit = the 100% factor from the service
    // (`maturity_adjustment === 1`). New rows persist this value instead of
    // null, mirroring what the dropdown shows by default. Falls back to "100"
    // until the service responds.
    const defaultMaturityCredit = useMemo(() => {
        if (maturityQuery.status !== "ready") return "100";
        const full = maturityQuery.data.find((f) => f.maturityAdjustment === 1);
        return full ? maturityPreset(full.maturityAdjustment).id : "100";
    }, [maturityQuery]);

    // State dropdown options from the real /v2/states backend (no hardcoded list).
    const statesQuery = useStates();
    const stateItems = useMemo(
        () => (statesQuery.status === "ready" ? statesQuery.data.map((s) => ({ id: s.code, label: s.code })) : []),
        [statesQuery],
    );

    /**
     * Display order + facility↔pricing pairing.
     *
     * `calcs` is index-aligned to `facilities`, so the two have to travel
     * through the sort together — sorting the facility array on its own would
     * shear each row away from its derived premiums. Archived rows sink to the
     * bottom (OR-1431); everything else keeps its position.
     */
    const rows = useMemo(
        () => sortArchivedLast(facilities.map((f, idx) => ({ f, calc: calcs[idx] ?? null })), (row) => row.f),
        [facilities, calcs],
    );

    // Footer aggregates. User-input census + licensed-bed columns come
    // from the SoT directly. Equivalency / premiums sum from `calcs`.
    // Null derivations contribute 0 to keep a single broken row from
    // blanking the entire footer.
    //
    // Archived facilities are skipped: they're excluded from everything
    // pricing-related (OR-1431), and the backend already leaves them out of the
    // premium totals it computes — a footer that still counted them would
    // disagree with Premium Selection.
    const totals = facilities.reduce(
        (acc, f, idx) => {
            if (f.isExcluded) return acc;
            const calc = calcs[idx];
            acc.skilled += num(f.censusSkilled);
            acc.assisted += num(f.censusAssisted);
            acc.independent += num(f.censusIndependent);
            acc.addlExposure += num(f.censusAddlExposure);
            acc.licSnf += num(f.licensedSnf);
            acc.licAlf += num(f.licensedAlf);
            acc.licIlf += num(f.licensedIlf);
            if (calc) {
                acc.equivalency += calc.equivalency;
                acc.unadjPrem += calc.unadjPremium ?? 0;
                acc.matAdjPrem += calc.matAdjPremium ?? 0;
                acc.sirAdjPrem += calc.sirAdjPremium ?? 0;
            }
            return acc;
        },
        {
            skilled: 0,
            assisted: 0,
            independent: 0,
            addlExposure: 0,
            equivalency: 0,
            unadjPrem: 0,
            matAdjPrem: 0,
            sirAdjPrem: 0,
            licSnf: 0,
            licAlf: 0,
            licIlf: 0,
        },
    );
    const totalLicGrand = totals.licSnf + totals.licAlf + totals.licIlf;

    // ── Mutation helpers — keep cell callsites short ─────────
    // Defers the SoT mutation off the keystroke (re-render + derived recalc run
    // at low priority); cells paint their local buffer instantly. Shared policy
    // lives in `useDeferredSetter`.
    const set = useDeferredSetter(update);
    /** Apply a patch that can move the row's State or Geo.
     *
     *  Those two compose the rates-table key, so an edit to either one
     *  means a rate the pricing-model import stored on this location no
     *  longer describes the row: drop it and let the lookup answer again.
     *  Compared against the row rather than cleared blind, so re-picking
     *  the value that is already there keeps the imported rate. */
    const setGeography = (f: Facility, patch: FacilityWritePayload) => {
        const rateCodeChanged =
            (patch.state !== undefined && patch.state !== f.state) ||
            (patch.geo !== undefined && patch.geo !== f.geo);
        set(f.id, rateCodeChanged ? { ...patch, actuarialRate: "" } : patch);
    };
    /** Auto-fill a row from a picked catalog facility. CMS number feeds
     *  Provider Number; beds only fill when present so a pick never wipes
     *  counts the user already typed. */
    const applyCatalogFacility = (f: Facility, cf: CatalogFacility) => {
        const numToStr = (n: number | null | undefined) => (n == null ? undefined : String(n));
        const patch: FacilityWritePayload = {
            // Persist the catalog link so the location carries `facility_id`
            // (OR-1417): without it, Add Row in Regulatory can't tell this
            // facility already exists and creates a duplicate location.
            catalogFacilityId: cf.id,
            name: cf.name ?? "",
            providerNumber: cf.cms_number ?? "",
            address: cf.address ?? "",
            city: cf.city ?? "",
            state: cf.state ?? "",
            zip: cf.zip_code ?? "",
            // County is stored as a FIPS code; Geo from urbanicity.
            county: cf.fips ?? "",
            geo: cf.urbanicity === "Urban" || cf.urbanicity === "Rural" ? cf.urbanicity : "",
        };
        const cs = numToStr(cf.skilled_beds);
        if (cs !== undefined) patch.censusSkilled = cs;
        const ca = numToStr(cf.assisted_beds);
        if (ca !== undefined) patch.censusAssisted = ca;
        const ci = numToStr(cf.independent_beds);
        if (ci !== undefined) patch.censusIndependent = ci;
        const ls = numToStr(cf.skilled_beds_licensed);
        if (ls !== undefined) patch.licensedSnf = ls;
        const la = numToStr(cf.assisted_beds_licensed);
        if (la !== undefined) patch.licensedAlf = la;
        const li = numToStr(cf.independent_beds_licensed);
        if (li !== undefined) patch.licensedIlf = li;
        setGeography(f, patch);
    };
    /** Picking a ZIP auto-fills its geography (OR-881 / OR-1362). State +
     *  County(FIPS) go in the SAME patch so CountySelect (scoped by state)
     *  can resolve the county name — this is the actual "county not
     *  populated" fix. Mirrors applyCatalogFacility's field handling. */
    const applyZip = (f: Facility, z: ZipCode) => {
        setGeography(f, {
            zip: z.zipCode,
            city: z.city ?? "",
            state: z.state ?? "",
            county: z.fips ?? "",
            geo: z.urbanicity === "Urban" || z.urbanicity === "Rural" ? z.urbanicity : "",
        });
    };
    const addRow = () => {
        // Seed Maturity Credit with the dropdown's default-selected value
        // (100% from the service) so the new row persists it, not null.
        void create({ maturityCredit: defaultMaturityCredit });
    };
    const removeRow = (id: string) => {
        void remove(id);
    };
    /** Archive / restore (OR-1431). Staged like every other Schedule edit —
     *  the row updates instantly, the Save button flushes the PATCH. */
    const setArchived = (id: string, isExcluded: boolean) => {
        void update(id, { isExcluded });
    };

    return (
        <FormGroup
            title="Facility Schedule"
            flushBody
            action={
                <Button color="link-gray" size="sm" onClick={addRow} data-autotest="schedule-facilities-add-row-button">
                    + Add Row
                </Button>
            }
        >
            <EditableCellProvider>
                <div className="relative overflow-hidden">
                <Table
                    aria-label="Facility Schedule"
                    wrapperClassName="max-h-[640px] min-h-96 overflow-auto border-b border-secondary"
                    className="[&_tbody_tr:last-child]:border-b-0!"
                >
                    <Table.Header className="bg-secondary sticky top-0 z-20">
                        <Table.Head id="rowNum" label="#" isRowHeader className={STICKY_NUM_HEAD} style={fixed(W.rowNum)} />
                        <Table.Head id="facilityName" label="Facility Name" className={STICKY_NAME_HEAD} style={fixed(W.facilityName)} />
                        <Table.Head id="providerNumber" label="Provider Number" style={fixed(W.providerNumber)} />
                        <Table.Head id="acquired" label="Acquired" style={fixed(W.acquired)} />
                        <Table.Head id="operator" label="Operator" style={fixed(W.operator)} />
                        <Table.Head id="address" label="Address" style={fixed(W.address)} />
                        <Table.Head id="city" label="City" style={fixed(W.city)} />
                        <Table.Head id="state" label="State" style={fixed(W.state)} />
                        <Table.Head id="zip" label="Zip" style={fixed(W.zip)} />
                        <Table.Head id="geo" label="Geo" style={fixed(W.geo)} />
                        <Table.Head id="county" label="County" style={fixed(W.county)} />
                        <Table.Head id="plRetro" label="PL Retro Date" style={fixed(W.plRetro)} />
                        <Table.Head id="glRetro" label="GL Retro Date" style={fixed(W.glRetro)} />
                        <Table.Head id="retention" label="Retention" style={fixed(W.retention)} />
                        <Table.Head id="plLimit" label="PL Limit" style={fixed(W.plLimit)} />
                        <Table.Head id="glLimit" label="GL Limit" style={fixed(W.glLimit)} />
                        <Table.Head id="occupancy" label="Occupancy" style={fixed(W.occupancy)} />
                        <Table.Head id="skilled" label="Skilled" style={fixed(W.skilled)} />
                        <Table.Head id="assisted" label="Assisted" style={fixed(W.assisted)} />
                        <Table.Head id="independent" label="Independent" style={fixed(W.independent)} />
                        <Table.Head id="addlExposure" label="Add'l. Exposure" style={fixed(W.addlExposure)} />
                        <Table.Head id="equivalency" label="Equivalency" style={fixed(W.equivalency)} />
                        <Table.Head id="rate" label="Rate" style={fixed(W.rate)} />
                        <Table.Head id="unadjPrem" label="Unadj. Prem." style={fixed(W.unadjPrem)} />
                        <Table.Head id="matCredit" label="Mat. Credit" style={fixed(W.matCredit)} />
                        <Table.Head id="matAdjPrem" label="Mat. Adj. Prem." style={fixed(W.matAdjPrem)} />
                        <Table.Head id="sirCredit" label="SIR Credit" style={fixed(W.sirCredit)} />
                        <Table.Head id="sirAdjPrem" label="SIR Adj. Prem." style={fixed(W.sirAdjPrem)} />
                        <Table.Head id="licSnf" label="Lic. SNF" style={fixed(W.licSnf)} />
                        <Table.Head id="licAlf" label="Lic. ALF" style={fixed(W.licAlf)} />
                        <Table.Head id="licIlf" label="Lic. ILF" style={fixed(W.licIlf)} />
                        <Table.Head id="totalLic" label="Total Lic." style={fixed(W.totalLic)} />
                        <Table.Head id="actions" label="" className={STICKY_RIGHT_HEAD} style={fixed(W.actions)} />
                    </Table.Header>
                    <Table.Body>
                        {rows.map(({ f, calc }, idx) => {
                            const archived = f.isExcluded;
                            return (
                                <Table.Row id={f.id} key={f.id} className={archived ? ARCHIVED_ROW : undefined}>
                                    <Table.Cell className={STICKY_NUM_CELL} style={fixed(W.rowNum)}>
                                        {idx + 1}
                                    </Table.Cell>
                                    <Table.Cell className={STICKY_NAME_CELL} style={fixed(W.facilityName)}>
                                        {archived ? (
                                            /* Archived rows carry the hover tooltip that explains
                                             * the state and points at Restore. It hangs off the
                                             * Facility Name cell — sticky, so it stays reachable
                                             * however far the table is scrolled sideways — and
                                             * re-enables pointer events, which the row class
                                             * switches off for every other cell. The name renders
                                             * as static text rather than the autocomplete: the row
                                             * is frozen until restored, so there is nothing to
                                             * search for. */
                                            <Tooltip title={ARCHIVED_TOOLTIP} placement="top">
                                                <TooltipTrigger className={ARCHIVED_TOOLTIP_TRIGGER}>
                                                    <span className="text-primary truncate text-sm">{f.name || "—"}</span>
                                                </TooltipTrigger>
                                            </Tooltip>
                                        ) : (
                                            /* The duplicate-submission flag now lives INSIDE
                                             * the Facility Name field (Andressa review) — the
                                             * standalone flag column was removed. Only shown
                                             * for facilities the backend marked as duplicates. */
                                            <div className="flex items-center gap-1.5">
                                                {f.duplicateSubmissionId && (
                                                    <FacilityFlagPopover
                                                        className="shrink-0"
                                                        // The submission that already carries this
                                                        // facility. The route's index child redirects
                                                        // to UW Information (OR-1442 — the CTA used to
                                                        // be a no-op).
                                                        onViewOriginal={() =>
                                                            navigate(`/submissions/${f.duplicateSubmissionId}`)
                                                        }
                                                    />
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <FacilityNameAutocomplete
                                                        ariaLabel="Facility Name"
                                                        placeholder="Type Facility Name"
                                                        value={f.name}
                                                        onChange={(v) => set(f.id, { name: v })}
                                                        onSelect={(cf) => applyCatalogFacility(f, cf)}
                                                        data-autotest="schedule-facility-schedule-facility-name-input"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.providerNumber)}>
                                        <EditableTextCell
                                            ariaLabel="Provider Number"
                                            placeholder="Provider Number"
                                            value={f.providerNumber}
                                            onChange={(v) => set(f.id, { providerNumber: v })}
                                            data-autotest="schedule-facility-schedule-provider-number-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.acquired)}>
                                        <EditableDateCell
                                            ariaLabel="Acquired"
                                            value={f.acquired}
                                            onChange={(v) => set(f.id, { acquired: v })}
                                            isInvalid={isInvalidDate(f.acquired)}
                                            errorMessage={DATE_FORMAT_ERROR}
                                            data-autotest="schedule-facility-schedule-acquired-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.operator)}>
                                        <EditableTextCell
                                            ariaLabel="Operator"
                                            placeholder="Operator"
                                            value={f.operator}
                                            onChange={(v) => set(f.id, { operator: v })}
                                            data-autotest="schedule-facility-schedule-operator-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.address)}>
                                        <EditableTextCell
                                            ariaLabel="Address"
                                            placeholder="Address"
                                            value={f.address}
                                            onChange={(v) => set(f.id, { address: v })}
                                            data-autotest="schedule-facility-schedule-address-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.city)}>
                                        <EditableTextCell
                                            ariaLabel="City"
                                            placeholder="City"
                                            value={f.city}
                                            onChange={(v) => set(f.id, { city: v })}
                                            data-autotest="schedule-facility-schedule-city-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.state)}>
                                        <InvalidShell isInvalid={false} reserveSpace={false}>
                                            <SearchSelect
                                                size="sm"
                                                aria-label="State"
                                                placeholder="—"
                                                selectedKey={f.state || null}
                                                onSelectionChange={(k) => setGeography(f, { state: (k as string) ?? "" })}
                                                items={stateItems}
                                                popoverWidthMul={1.2}
                                                data-autotest="schedule-facility-schedule-state-select"
                                            >
                                                {(item) => (
                                                    <SearchSelect.Item
                                                        id={item.id}
                                                        data-autotest={`schedule-facility-schedule-state-select-option-${item.id}`}
                                                    >
                                                        {item.label}
                                                    </SearchSelect.Item>
                                                )}
                                            </SearchSelect>
                                        </InvalidShell>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.zip)}>
                                        <ZipCodeAutocomplete
                                            ariaLabel="Zip"
                                            placeholder="Zip"
                                            value={f.zip}
                                            onChange={(v) => set(f.id, { zip: v })}
                                            onSelect={(z) => applyZip(f, z)}
                                            data-autotest="schedule-facility-schedule-zip-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.geo)}>
                                        <InvalidShell isInvalid={false} reserveSpace={false}>
                                            <Select
                                                size="sm"
                                                aria-label="Geo"
                                                placeholder="—"
                                                selectedKey={f.geo || null}
                                                onSelectionChange={(k) => {
                                                    const next = (k as string) ?? "";
                                                    setGeography(f, { geo: next === "Rural" || next === "Urban" ? next : "" });
                                                }}
                                                items={GEO_ITEMS}
                                                data-autotest="schedule-facility-schedule-geo-select"
                                            >
                                                {(item) => <Select.Item id={item.id} data-autotest={`schedule-facility-schedule-geo-select-option-${item.id}`}>{item.label}</Select.Item>}
                                            </Select>
                                        </InvalidShell>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.county)}>
                                        {/* County SearchSelect: stores the FIPS code, shows the
                                          * name, list scoped to the row's State. Still auto-fills
                                          * from a facility pick (which sets State + FIPS). */}
                                        <CountySelect
                                            ariaLabel="County"
                                            value={f.county}
                                            state={f.state}
                                            onChange={(fips) => set(f.id, { county: fips })}
                                            data-autotest="schedule-facility-schedule-county-select"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.plRetro)}>
                                        <EditableDateCell
                                            ariaLabel="PL Retro Date"
                                            value={f.plRetroactiveDate}
                                            onChange={(v) => set(f.id, { plRetroactiveDate: v })}
                                            isInvalid={isInvalidDate(f.plRetroactiveDate)}
                                            errorMessage={DATE_FORMAT_ERROR}
                                            data-autotest="schedule-facility-schedule-pl-retro-date-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.glRetro)}>
                                        <EditableDateCell
                                            ariaLabel="GL Retro Date"
                                            value={f.glRetroactiveDate}
                                            onChange={(v) => set(f.id, { glRetroactiveDate: v })}
                                            isInvalid={isInvalidDate(f.glRetroactiveDate)}
                                            errorMessage={DATE_FORMAT_ERROR}
                                            data-autotest="schedule-facility-schedule-gl-retro-date-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.retention)}>
                                        <InvalidShell isInvalid={false} reserveSpace={false}>
                                            <MoneySelect
                                                aria-label="Retention"
                                                placeholder="Select retention"
                                                presets={RETENTION_PRESETS}
                                                isClearable
                                                value={f.retention}
                                                onChange={(v) =>
                                                    // A new retention invalidates the imported SIR
                                                    // credit, which was priced against the old one.
                                                    set(f.id, v === f.retention ? { retention: v } : { retention: v, sirCredit: "" })
                                                }
                                                isInvalid={false}
                                                data-autotest="schedule-facility-schedule-retention-select"
                                            />
                                        </InvalidShell>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.plLimit)}>
                                        <EditableTextCell
                                            ariaLabel="PL Limit"
                                            placeholder="$0.00"
                                            value={f.plLimit}
                                            onChange={(v) => set(f.id, { plLimit: v })}
                                            inputMode="decimal"
                                            sanitize={moneyDigits}
                                            format={formatMoneyMask}
                                            data-autotest="schedule-facility-schedule-pl-limit-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.glLimit)}>
                                        <EditableTextCell
                                            ariaLabel="GL Limit"
                                            placeholder="$0.00"
                                            value={f.glLimit}
                                            onChange={(v) => set(f.id, { glLimit: v })}
                                            inputMode="decimal"
                                            sanitize={moneyDigits}
                                            format={formatMoneyMask}
                                            data-autotest="schedule-facility-schedule-gl-limit-input"
                                        />
                                    </Table.Cell>

                                    {/* Census is user-entered; Occupancy + every premium
                                      * column derives live from useFacilitiesCalc. */}
                                    <Table.Cell style={fixed(W.occupancy)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-occupancy-cell">
                                            {displayPercentFraction(calc?.occupancy)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.skilled)}>
                                        <EditableTextCell
                                            ariaLabel="Skilled"
                                            placeholder="0"
                                            value={f.censusSkilled}
                                            onChange={(v) => set(f.id, { censusSkilled: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-census-skilled-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.assisted)}>
                                        <EditableTextCell
                                            ariaLabel="Assisted"
                                            placeholder="0"
                                            value={f.censusAssisted}
                                            onChange={(v) => set(f.id, { censusAssisted: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-census-assisted-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.independent)}>
                                        <EditableTextCell
                                            ariaLabel="Independent"
                                            placeholder="0"
                                            value={f.censusIndependent}
                                            onChange={(v) => set(f.id, { censusIndependent: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-census-independent-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.addlExposure)}>
                                        <EditableTextCell
                                            ariaLabel="Additional Exposure"
                                            placeholder="0"
                                            value={f.censusAddlExposure}
                                            onChange={(v) => set(f.id, { censusAddlExposure: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-census-additional-exposure-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.equivalency)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-equivalency-cell">
                                            {displayDecimal(calc?.equivalency, 2)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.rate)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-rate-cell">
                                            {displayMoney(calc?.rate)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.unadjPrem)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-unadj-prem-cell">
                                            {displayMoneyCents(calc?.unadjPremium)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.matCredit)}>
                                        {/* Dropdown loaded from /v2/credit-factors/claims-made,
                                          * default 100%. "Custom Value" still allows fractions. */}
                                        <SelectWithCustom
                                            aria-label="Maturity Credit"
                                            value={f.maturityCredit || defaultMaturityCredit}
                                            onChange={(v) => set(f.id, { maturityCredit: v })}
                                            presets={maturityPresets}
                                            formatCustomLabel={(v) => (v.trim().endsWith("%") ? v : `${v}%`)}
                                            data-autotest="schedule-facility-schedule-maturity-credit-select"
                                            dataAutotestPrefix="schedule-facility-schedule-maturity-credit-select"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.matAdjPrem)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-mat-adj-prem-cell">
                                            {displayMoney(calc?.matAdjPremium)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.sirCredit)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-sir-credit-cell">
                                            {displayPercentFraction(calc?.sirCredit, 1)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.sirAdjPrem)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-sir-adj-prem-cell">
                                            {displayMoney(calc?.sirAdjPremium)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.licSnf)}>
                                        <EditableTextCell
                                            ariaLabel="Licensed SNF"
                                            placeholder="0"
                                            value={f.licensedSnf}
                                            onChange={(v) => set(f.id, { licensedSnf: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-licensed-snf-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.licAlf)}>
                                        <EditableTextCell
                                            ariaLabel="Licensed ALF"
                                            placeholder="0"
                                            value={f.licensedAlf}
                                            onChange={(v) => set(f.id, { licensedAlf: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-licensed-alf-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.licIlf)}>
                                        <EditableTextCell
                                            ariaLabel="Licensed ILF"
                                            placeholder="0"
                                            value={f.licensedIlf}
                                            onChange={(v) => set(f.id, { licensedIlf: v })}
                                            inputMode="numeric"
                                            sanitize={integer}
                                            data-autotest="schedule-facility-schedule-licensed-ilf-input"
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.totalLic)}>
                                        <span className={READONLY_CELL} data-autotest="schedule-facility-schedule-total-licensed-cell">
                                            {displayInt(calc?.totalLicensed)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell className={STICKY_RIGHT_CELL} style={fixed(W.actions)}>
                                        <div className="flex justify-end">
                                            <FacilityRowActionsMenu
                                                isArchived={archived}
                                                facilityName={f.name}
                                                onArchive={() => setArchived(f.id, true)}
                                                onRestore={() => setArchived(f.id, false)}
                                                onDelete={() => removeRow(f.id)}
                                                data-autotest="schedule-facility-schedule-row-actions-trigger"
                                            />
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}

                        {facilities.length > 0 && (
                            <Table.Row id="__totals__" className="bg-secondary font-semibold">
                                <Table.Cell className="md:sticky md:left-0 md:z-10 md:bg-secondary" style={fixed(W.rowNum)} />
                                <Table.Cell
                                    className="md:sticky md:left-[48px] md:z-10 md:bg-secondary text-quaternary text-xs uppercase tracking-wider"
                                    style={fixed(W.facilityName)}
                                >
                                    Total
                                </Table.Cell>
                                <Table.Cell style={fixed(W.providerNumber)} />
                                <Table.Cell style={fixed(W.acquired)} />
                                <Table.Cell style={fixed(W.operator)} />
                                <Table.Cell style={fixed(W.address)} />
                                <Table.Cell style={fixed(W.city)} />
                                <Table.Cell style={fixed(W.state)} />
                                <Table.Cell style={fixed(W.zip)} />
                                <Table.Cell style={fixed(W.geo)} />
                                <Table.Cell style={fixed(W.county)} />
                                <Table.Cell style={fixed(W.plRetro)} />
                                <Table.Cell style={fixed(W.glRetro)} />
                                <Table.Cell style={fixed(W.retention)} />
                                <Table.Cell style={fixed(W.plLimit)} />
                                <Table.Cell style={fixed(W.glLimit)} />
                                <Table.Cell style={fixed(W.occupancy)} />
                                <Table.Cell style={fixed(W.skilled)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.skilled)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.assisted)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.assisted)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.independent)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.independent)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.addlExposure)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.addlExposure)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.equivalency)}>
                                    <span className="text-primary text-sm tabular-nums">{displayDecimal(totals.equivalency, 2)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.rate)} />
                                <Table.Cell style={fixed(W.unadjPrem)}>
                                    <span className="text-primary text-sm tabular-nums">{displayMoneyCents(totals.unadjPrem)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.matCredit)} />
                                <Table.Cell style={fixed(W.matAdjPrem)}>
                                    <span className="text-primary text-sm tabular-nums">{displayMoney(totals.matAdjPrem)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.sirCredit)} />
                                <Table.Cell style={fixed(W.sirAdjPrem)}>
                                    <span className="text-primary text-sm tabular-nums">{displayMoney(totals.sirAdjPrem)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.licSnf)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.licSnf)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.licAlf)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.licAlf)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.licIlf)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totals.licIlf)}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.totalLic)}>
                                    <span className="text-primary text-sm tabular-nums">{displayInt(totalLicGrand)}</span>
                                </Table.Cell>
                                <Table.Cell className="md:sticky md:right-0 md:z-10 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]" style={fixed(W.actions)} />
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
                {facilities.length === 0 && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="pointer-events-auto">
                            <TableEmptyState
                                icon={Building07}
                                title="Add Facilities"
                                description="Use the button below to start building the Facility Schedule"
                                onAdd={addRow}
                                data-autotest="schedule-facility-schedule-empty-state-add-row"
                            />
                        </div>
                    </div>
                )}
                {facilities.length > 0 && (
                    <>
                        <div
                            aria-hidden="true"
                            className="bg-border-secondary pointer-events-none absolute top-0 bottom-0 z-10 hidden w-px md:block"
                            style={{ left: W.rowNum + W.facilityName }}
                        />
                        <div
                            aria-hidden="true"
                            className="bg-border-secondary pointer-events-none absolute top-0 bottom-0 z-10 hidden w-px md:block"
                            style={{ right: W.actions }}
                        />
                    </>
                )}
                </div>
            </EditableCellProvider>
        </FormGroup>
    );
}
